import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

interface ErrorBoundaryProps {
  children: ReactNode;
  // When this value changes (e.g. the route path), a previously-caught error is
  // cleared so the next page can render. It is intentionally NOT used as a React
  // `key`: keying would remount the whole subtree on every navigation (the
  // installation container would refetch on each tab switch). Here, children only
  // unmount while an error is shown, and remount fresh on the next navigation.
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  lastResetKey?: string;
}

// App-wide safety net. Before this, any render exception (e.g. reading a field that
// does not exist for a given product) blanked the entire page AND removed the
// sidebar/header, leaving no way to navigate away. Wrapping the routed content keeps
// the nav alive and shows a recoverable message instead of a white screen.
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    lastResetKey: this.props.resetKey
  };

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey !== state.lastResetKey) {
      return { hasError: false, lastResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the app alive; log for debugging instead of crashing to a blank page.
    console.error('Render error caught by ErrorBoundary:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 1 }}>
            <FormattedMessage
              id="somethingWentWrong"
              defaultMessage="Something went wrong"
            />
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#777' }}>
            <FormattedMessage
              id="somethingWentWrongHint"
              defaultMessage="This section could not be displayed. Use the menu to open another page, or reload."
            />
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{
              backgroundColor: '#ffc04d',
              color: '#000000',
              '&:hover': { bgcolor: '#f7b34d' }
            }}
          >
            <FormattedMessage id="reload" defaultMessage="Reload" />
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
